"use client"

import { useDispatch, useSelector } from "react-redux"
import SimpleBar from "simplebar-react"
import { useState } from "react"
import { Bell, MessageCircle, Circle, User, Clock } from 'lucide-react'
import {
  getNotificationListAction,
  getOrderMessageListAction,
  updateNotificationStatusAction,
} from "../../../../services/action/common"
import FullScreenModal from "../../../utils/modals/FullScreenModal"
import { returnDateWithTime } from "../../../utils/functions/functions"
import LiveChatInternal from "../../../live-internal-chat-users/LiveChatInternal"

const NotificationList = () => {
  const { notificationList } = useSelector((state) => state.commonReducer)
  const { user } = useSelector((state) => state.authenticationReducer)
  const [openLiveChat, setopenLiveChat] = useState(false)
  const dispatch = useDispatch()
  const userType = user?.userType?.toLowerCase()

  const cbNotification = () => {
    dispatch(getNotificationListAction({ userType }))
  }

  const handleOpenChat = (leadId, isNew) => {
    if (isNew) {
      dispatch(
        updateNotificationStatusAction({
          userType,
          data: { leadId: leadId },
          cb: cbNotification,
        }),
      )
    }
    dispatch(getOrderMessageListAction({ data: { leadId: leadId } }))
  }

  const startOpenChat = ({ userId, isNew }) => {
    setopenLiveChat(userId)
    if (isNew) {
      dispatch(
        updateNotificationStatusAction({
          userType,
          data: { userId },
          cb: cbNotification,
        }),
      )
    }
  }

  // Empty state component
  const EmptyNotifications = () => (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <div className="mb-3">
        <Bell size={40} className="text-gray-400" />
      </div>
      <p className="text-gray-200 font-medium mb-1">No notifications</p>
      <p className="text-gray-400 text-xs">We'll notify you when something arrives</p>
    </div>
  )

  return (
    <>
      <SimpleBar style={{ maxHeight: "350px" }} className="overflow-y-auto">
        {notificationList?.data?.length > 0 ? (
          <div className="divide-y divide-gray-700/30">
            {notificationList?.data?.map((item, index) => (
              <div
                key={index}
                className={`group relative transition-all duration-200 ${
                  !item?.isSeen ? "bg-blue-500/10" : ""
                } hover:bg-gray-700/30 cursor-pointer`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (item?.messageType === "EXTERNAL") {
                    startOpenChat({
                      userId: item?.userId,
                      isNew: !item?.isSeen,
                      notificationId: item?._id,
                      visitorId: item?.visitorId,
                    });
                  } else {
                    handleOpenChat(item?.leadId, !item?.isSeen);
                  }
                }}
              >
                <div className="block p-3">
                  <div className="flex items-center">
                    {/* Unread indicator */}
                    <div className="flex-shrink-0 mr-3">
                      {!item?.isSeen ? (
                        <div className="h-2.5 w-2.5 rounded-full bg-blue-400 ring-2 ring-blue-400/30"></div>
                      ) : (
                        <div className="h-2.5 w-2.5 rounded-full bg-gray-500/50"></div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h5 className={`text-sm font-medium ${!item?.isSeen ? 'text-white' : 'text-gray-300'} truncate pr-2`}>
                          {item?.senderName}
                        </h5>
                        
                        {/* Message count badge */}
                        {item?.messageNewMessageCount > 0 && (
                          <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-white bg-blue-500 rounded-full">
                            {item?.messageNewMessageCount}
                          </span>
                        )}
                      </div>
                      
                      {/* Order/Visitor ID */}
                      <p className="text-xs text-gray-400 mb-0.5 truncate">
                        #{item?.orderId || item?.visitorId}
                      </p>

                      <p className="text-xs text-gray-400 mb-0.5 truncate">
                        {item?.content}
                      </p>
                      
                      {/* Timestamp */}
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1 text-gray-500" />
                        <span>{returnDateWithTime(item?.lastMessageTime || new Date())}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyNotifications />
        )}
      </SimpleBar>

      <FullScreenModal
        size="xl"
        open={openLiveChat}
        maxWidth="max-width-modal-dialog"
        content={() => (
          <LiveChatInternal userId={openLiveChat} setopenLiveChat={setopenLiveChat} isContentOnly={true} />
        )}
        onClose={() => setopenLiveChat(false)}
      />
    </>
  )
}

export default NotificationList